import { useGo } from '../../app/navigation';
import { openMicSetup } from '../../app/uiState';
import { FACTS_ROUTE, factOfDay } from '../../content/facts';
import { lessonsOf } from '../../content/lessons';
import { SONGS } from '../../content/songs';
import { levelInfo, MODULE_IDS, type ModuleId, streak, todayXp, useProgress, useSettings } from '../../core/store';
import { Btn } from '../../ui/Button';
import { Icon, type IconName } from '../../ui/icons';
import { richText } from '../../ui/Rich';
import { modStyle } from '../../ui/style';
import { ProgressBar, Ring } from '../../ui/widgets';
import { FactCard } from '../facts/FactsSection';
import { MODULE_INFO } from '../module/info';
import { moduleStats, nextStepOf } from '../module/ModulePage';
import { MODULE_DEFS } from '../registry';
import { StringsGame } from './StringsGame';

interface NextStep {
  k: string;
  t: string;
  d: string;
  route: string;
}

function nextStep(xp: number, modXp: Record<string, number>): NextStep {
  const order: ModuleId[] = ['gitar', 'nota', 'akor', 'vokal'];
  if (!xp)
    return {
      k: 'Buradan başla',
      t: 'Kulak eğitimi nasıl çalışır?',
      d: 'Duyduğunu Çal · Ders 1',
      route: 'gitar.ders.g1',
    };
  const started = order.filter((m) => (modXp[m] ?? 0) > 0);
  for (const m of [...started, ...order.filter((x) => !started.includes(x))]) {
    const defs = MODULE_DEFS[m];
    const ns = nextStepOf(m, defs.levels);
    const title = MODULE_INFO[m].title;
    const lesIdx = ns.lesson ? lessonsOf(m).indexOf(ns.lesson) : -1;
    if (ns.level && (!ns.lesson || lesIdx > 2))
      return {
        k: 'Sıradaki antrenman',
        t: ns.level.title,
        d: `${title} · Seviye ${ns.level.n}`,
        route: ns.level.route,
      };
    if (ns.lesson)
      return {
        k: 'Sıradaki ders',
        t: richText(ns.lesson.title),
        d: `${title} · Ders ${lesIdx + 1}`,
        route: `${m}.ders.${ns.lesson.id}`,
      };
    if (ns.level)
      return {
        k: 'Sıradaki antrenman',
        t: ns.level.title,
        d: `${title} · Seviye ${ns.level.n}`,
        route: ns.level.route,
      };
  }
  return { k: 'Meydan okuma', t: 'Günün melodileri', d: 'Duyduğunu Çal', route: 'gitar.meydan.gunun' };
}

function JamPromo() {
  const go = useGo();
  const artists = Array.from(new Set(SONGS.flatMap((s) => (s.cover ? [s.cover.artist] : []))));
  return (
    <button type="button" className="jam-promo" onClick={() => go('jam')}>
      <span className="jp-ic">
        <Icon name="jam" />
      </span>
      <span className="jp-text">
        <span className="jp-k">Jam</span>
        <span className="jp-t">Akorlar aksın, sen çal</span>
        <span className="jp-d">
          {artists.length ? `Sevilen şarkılar (${artists.join(', ')}) ve ` : ''}kulaktan.com'un kendi şarkılarıyla oyun
          gibi çal. Doğru çaldıkça çarpan ×8'e kadar katlanır.
        </span>
      </span>
      <span className="jp-mult" aria-hidden="true">
        ×8
      </span>
    </button>
  );
}

const TOOLS: [string, IconName, string, string][] = [
  ['araclar.akort', 'tuner', 'Akort aleti', 'Gitarını mikrofonla akort et'],
  ['araclar.metronom', 'metronome', 'Metronom', 'Tempo ve vuruş çalışması'],
  ['araclar.kutuphane', 'chordbox', 'Akor kütüphanesi', 'Şemalar ve sesleri'],
  ['araclar.analiz', 'wave', 'Canlı ses analizi', 'Perde, sent ve vibrato'],
  ['vokal.aralik-testi', 'voice', 'Ses aralığı testi', 'Ses tipini öğren'],
];

export function HomePage() {
  const go = useGo();
  const progress = useProgress();
  const settings = useSettings();
  const ns = nextStep(progress.xp, progress.modXp);
  const lv = levelInfo();
  const goal = settings.dailyGoal || 60;
  const today = todayXp();
  const st = streak();
  return (
    <>
      <section className="hero">
        <div className="hero-text">
          <div className="eyebrow">Gitar ve vokal için kulak eğitimi</div>
          <h1 className="hero-title">
            Duyduğunu çal.
            <br />
            <span className="hl">Duyduğunu söyle.</span>
          </h1>
          <p className="hero-lead">
            Kulaktan melodi çıkar, notaları tanı, sesini çalıştır ve şarkıların akorlarını kendin bul. Mikrofon
            çaldığını dinler, nerede yanıldığını anında söyler.
          </p>
          <div className="hero-cta">
            <Btn kind="primary" size="lg" icon="right" onClick={() => go(ns.route)}>
              {ns.k === 'Buradan başla' ? 'İlk derse başla' : 'Devam et'}
            </Btn>
            <Btn kind="ghost" size="lg" icon="mic" onClick={openMicSetup}>
              Mikrofonu kur
            </Btn>
          </div>
        </div>
        <StringsGame />
      </section>
      <section className="today">
        <div className="today-item">
          <div className="ti-icon">
            <Icon name="flame" />
          </div>
          <div className="ti-text">
            <span className="ti-k">Çalışma serisi</span>
            <span className="ti-v">{st} gün</span>
            <span className="ti-k">{today ? 'Bugün çalıştın' : 'Bugün henüz çalışmadın'}</span>
          </div>
        </div>
        <div className="today-item">
          <Ring frac={today / goal} size={40} />
          <div className="ti-text">
            <span className="ti-k">Günlük hedef</span>
            <span className="ti-v">
              {Math.min(today, goal)} / {goal} XP
            </span>
          </div>
        </div>
        <div className="today-item">
          <div className="ti-icon">
            <Icon name="award" />
          </div>
          <div className="ti-text">
            <span className="ti-k">Seviye {lv.n}</span>
            <span className="ti-v">{lv.name}</span>
            <ProgressBar frac={lv.frac} />
          </div>
        </div>
      </section>
      <section className="sec">
        <button type="button" className="next-step" onClick={() => go(ns.route)}>
          <div className="ns-text">
            <span className="ns-k">{ns.k}</span>
            <span className="ns-t">{ns.t}</span>
            <span className="ti-k">{ns.d}</span>
          </div>
          <Icon name="right" />
        </button>
      </section>
      <section className="sec">
        <JamPromo />
      </section>
      <section className="sec">
        <h2 className="sec-title">Bölümler</h2>
        <div className="mod-grid">
          {MODULE_IDS.map((m) => {
            const inf = MODULE_INFO[m];
            const defs = MODULE_DEFS[m];
            const s = moduleStats(m, defs.levels);
            const frac = (s.done / Math.max(1, s.total)) * 0.5 + (s.stars / Math.max(1, s.starsTotal)) * 0.5;
            const kick = `${s.total} ders · ${defs.levels.length} antrenman${defs.challenges.length ? ` · ${defs.challenges.length} meydan okuma` : ''}`;
            return (
              <button key={m} type="button" className="mod-card" style={modStyle(m)} onClick={() => go(m)}>
                <div className="mc-top">
                  <div className="mc-ic">
                    <Icon name={inf.icon} />
                  </div>
                  <div>
                    <h3>{inf.title}</h3>
                    <div className="mc-kicker">{kick}</div>
                  </div>
                </div>
                <p>{inf.lead}</p>
                <div className="mc-foot">
                  <ProgressBar frac={frac} />
                  <span>
                    {s.done}/{s.total} ders · {s.stars} yıldız
                  </span>
                </div>
              </button>
            );
          })}
        </div>
      </section>
      <section className="sec home-fact">
        <div className="sec-row">
          <h2 className="sec-title">Biliyor muydun?</h2>
          <button type="button" className="sec-link" onClick={() => go(FACTS_ROUTE)}>
            <span>Az Bilinenler</span>
            <Icon name="right" />
          </button>
        </div>
        <FactCard fact={factOfDay()} />
      </section>
      <section className="sec">
        <h2 className="sec-title">Araçlar</h2>
        <div className="tools-row">
          {TOOLS.map(([r, ic, t, d]) => (
            <button key={r} type="button" className="tool-card" onClick={() => go(r)}>
              <span className="tc-ic">
                <Icon name={ic} />
              </span>
              <span>
                {t}
                <small>{d}</small>
              </span>
            </button>
          ))}
        </div>
      </section>
    </>
  );
}
